import { useState } from 'react';
import { Button, IconArrowLeft, IconArrowRight, Illustration, Typography, Wrapper } from '@screentone/core';
import { isSameDay } from 'date-fns';

import { ResponsiveLoader } from 'components';
import { RevisionSelector } from 'components/revision-selector/RevisionSelector';
import {
  ArtifactType,
  GetArtifactSettingHistoryQuery,
  useGetArtifactSettingHistoryQuery
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { EditorLayout } from 'utils/layoutUtils';
import { HistoryEntry } from './HistoryEntry';
import styles from './LayoutHistory.module.scss';

type LayoutHistoryItem = NonNullable<GetArtifactSettingHistoryQuery['getArtifactSettingHistory']>[number];

interface LayoutHistoryProps {
  artifactId: string;
}

export const LayoutHistory = ({ artifactId }: LayoutHistoryProps) => {
  const currentProperty = useConvertedProperty();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState<number | null>(Date.now());

  const { data, isLoading } = useGetArtifactSettingHistoryQuery(
    { publicationKey: currentProperty!, artifactId, artifactType: ArtifactType.Layout },
    { enabled: !!currentProperty && !!artifactId }
  );

  const history: LayoutHistoryItem[] = data?.getArtifactSettingHistory ?? [];

  if (isLoading) {
    return <ResponsiveLoader />;
  }

  if (!history.length) {
    return (
      <>
        <Typography variant="header3" margin={{ bottom: 'md' }}>
          Publish History
        </Typography>
        <Illustration name="no-data" className={styles.illustration} />
        <Typography componentEl="p" size="sm" align="center">
          This content has never been published!
        </Typography>
      </>
    );
  }

  const current = history[currentIndex];
  const hasPrev = currentIndex < history.length - 1;
  const hasNext = currentIndex > 0;

  const handleReset = () => {
    setSelectedDate(Date.now());
    setCurrentIndex(0);
  };

  const handleSelectDate = (dateNum: number) => {
    setSelectedDate(dateNum);
  };

  const handleSelectRevision = (createdUtc?: number) => {
    const idx = history.findIndex((item) => item.createdUtc === createdUtc);
    if (idx >= 0) setCurrentIndex(idx);
  };

  const publishedDates = history
    .filter((item) => isSameDay(item.createdUtc, selectedDate ?? Date.now()))
    .map((item) => item.createdUtc);

  // body is a union in the schema but this query is scoped to ArtifactType.Layout
  const layout = current.body as EditorLayout;

  return (
    <div>
      <Typography margin={{ bottom: 'md' }} variant="header3">
        Publish History
      </Typography>
      <Wrapper className={styles.navigator}>
        <Button tertiary onClick={() => setCurrentIndex((i) => i + 1)} disabled={!hasPrev}>
          <IconArrowLeft margin={{ right: 'sm' }} />
          Previous
        </Button>
        <RevisionSelector
          selectedDate={selectedDate}
          onSelectRevision={handleSelectRevision}
          onSelectDate={handleSelectDate}
          onReset={handleReset}
          dateGroups={[{ title: 'Published', type: 'published', dates: publishedDates }]}
        />
        <Button tertiary onClick={() => setCurrentIndex((i) => i - 1)} disabled={!hasNext}>
          Next
          <IconArrowRight margin={{ left: 'sm' }} />
        </Button>
      </Wrapper>
      <HistoryEntry createdUtc={current.createdUtc} djUserId={current.creatorUser.djUserId} layout={layout} />
    </div>
  );
};
